import React, { useRef, useEffect } from 'react';

interface InputBoxProps {
  word: string;
  userInput: string[];
  activeInputIndex: number;
  feedback: { visible: boolean; correct: boolean; correctWord: string };
  handleInputChange: (index: number, value: string) => void;
  handleKeyDown: (event: React.KeyboardEvent, index: number) => void;
  handleInputClick: (index: number) => void;
}

const InputBox: React.FC<InputBoxProps> = ({
  word,
  userInput,
  activeInputIndex,
  feedback,
  handleInputChange,
  handleKeyDown,
  handleInputClick,
}) => {
  const numberOfInputs = word.length;
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array.from({ length: numberOfInputs }).map(() => null));

  useEffect(() => {
    // Use optional chaining for safer access
    inputRefs.current[activeInputIndex]?.focus();
  }, [activeInputIndex]);

  return (
    <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 my-6 max-w-full">
      {word.split('').map((letter, index) => (
        <div 
          key={index}
          className="relative"
        >
          {letter === ' ' ? (
            <div className="w-6 h-10 sm:h-12 md:h-14 flex items-center justify-center">
              <div className="w-4 h-1 bg-gray-400 rounded-full"></div>
            </div>
          ) : (
            <input
              type="text"
              className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 text-center text-lg sm:text-xl md:text-2xl font-medium border-2 rounded-md focus:outline-none focus:ring-2 ${
                // Only apply feedback styling when feedback is visible
                feedback.visible 
                  ? feedback.correctWord[index].toLowerCase() === (userInput[index] || '').toLowerCase()
                    ? 'border-green-500 bg-green-50 text-green-700' // Correct letter
                    : 'border-red-500 bg-red-50 text-red-700'       // Incorrect letter
                  : index === activeInputIndex
                    ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-300' // Active input
                    : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500' // Inactive input
              }`}
              value={userInput[index] || ''}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onClick={() => handleInputClick(index)}
              ref={el => { inputRefs.current[index] = el; }}
              maxLength={1}
              disabled={feedback.visible}
              readOnly={true} // Prevent mobile OS keyboard from appearing
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default InputBox;
